import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2 } from 'lucide-react';
import type { BaselineData } from '@/pages/Onboarding';

interface BaselineQuestionsProps {
  onComplete: (data: BaselineData) => void;
  isLoading: boolean;
}

const AI_PLATFORMS = [
  { value: 'ChatGPT', label: 'ChatGPT' },
  { value: 'Gemini', label: 'Gemini' },
  { value: 'Claude', label: 'Claude' },
  { value: 'Copilot', label: 'Copilot' },
  { value: 'Perplexity', label: 'Perplexity' },
  { value: 'Other', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: '0', label: '0 days' },
  { value: '1-2', label: '1-2 days' },
  { value: '3-4', label: '3-4 days' },
  { value: '5+', label: '5+ days' },
];

export function BaselineQuestions({ onComplete, isLoading }: BaselineQuestionsProps) {
  const [platform, setPlatform] = useState('');
  const [platformOther, setPlatformOther] = useState('');
  const [frequency, setFrequency] = useState('');

  const isValid = platform && frequency && (platform !== 'Other' || platformOther.trim());

  const handleSubmit = () => {
    if (!isValid) return;
    
    onComplete({
      work_ai_platform: platform,
      work_ai_platform_other: platform === 'Other' ? platformOther : '',
      ai_weekly_frequency: frequency,
      ai_comfort_1_10: 0,
    });
  };

  return (
    <Card className="border-primary/20 bg-card/80 backdrop-blur">
      <CardHeader>
        <CardTitle>Quick AI Baseline</CardTitle>
        <CardDescription>
          Help us understand your current AI usage so we can personalize your experience
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Platform question */}
        <div className="space-y-3">
          <Label className="text-base font-medium">
            What AI platform do you use most often for work?
          </Label>
          <RadioGroup value={platform} onValueChange={setPlatform} className="grid grid-cols-2 gap-2">
            {AI_PLATFORMS.map((p) => (
              <div key={p.value} className="flex items-center space-x-2">
                <RadioGroupItem value={p.value} id={`platform-${p.value}`} />
                <Label htmlFor={`platform-${p.value}`} className="cursor-pointer">
                  {p.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {platform === 'Other' && (
            <Input
              placeholder="Which platform?"
              value={platformOther}
              onChange={(e) => setPlatformOther(e.target.value)}
              className="mt-2"
            />
          )}
        </div>

        {/* Frequency question */}
        <div className="space-y-3">
          <Label className="text-base font-medium">
            How often do you use AI at work during the week?
          </Label>
          <RadioGroup value={frequency} onValueChange={setFrequency} className="grid grid-cols-2 gap-2">
            {FREQUENCY_OPTIONS.map((f) => (
              <div key={f.value} className="flex items-center space-x-2">
                <RadioGroupItem value={f.value} id={`freq-${f.value}`} />
                <Label htmlFor={`freq-${f.value}`} className="cursor-pointer">
                  {f.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <Button 
          onClick={handleSubmit} 
          className="w-full" 
          size="lg"
          disabled={!isValid || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Continue to Prompt Builder'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}